import constants from './constants';
import i18n from '../../../app/i18n';
import { Fetcher } from '../../../lib/helpers';

const getTileData = (tileId, reactComp) => {
  const stateTiles = reactComp.props.tiles;
  const activeStatus = reactComp.props.activeStatus;
  const tilesFromStatus = stateTiles[activeStatus].tiles;
  const tileIndex = tilesFromStatus.findIndex(element => element.id === tileId);

  if (tileIndex != -1) {
    return {
      stateTiles,
      selectTile: tilesFromStatus[tileIndex],
      selectTileIndex: tileIndex,
    };
  }

  throw new Error('Tile not found');
};

const fetchTileJson = (tileId, cb) => {
  Fetcher.xmlHttpRequest({
    path: `/api/client_admin/tiles/${tileId}`,
    method: 'GET',
    success: cb,
  });
};

const updateTileStatus = (tileId, newStatus, cb) => {
  Fetcher.xmlHttpRequest({
    method: 'PUT',
    path: `/api/client_admin/tiles/${tileId}`,
    params: { newStatus },
    success: cb,
  });
};

const updateJquerySend = (count, draft) => {
  // Extraneous code used to patch connection between jQuery and React -- Delete when Share is moved to Edit
  const number = draft ? count + 1 : count - 1;
  window.Airbo.PubSub.publish('updateShareTabNotification', { number });
};

const performDbAction = (tileId, action, cb) => {
  Fetcher.xmlHttpRequest({
    method: constants.MENU_OPTS[action].method,
    path: `/api/client_admin/tiles/${tileId}/${constants.MENU_OPTS[action].url}`,
    success: cb,
  });
};

const deleteTile = (tileId, action, cb) => {
  Fetcher.xmlHttpRequest({
    method: constants.MENU_OPTS[action].method,
    path: `/api/client_admin/tiles/${constants.MENU_OPTS[action].url}`,
    params: { ids: tileId },
    success: cb,
  });
};

class TileManager {
  constructor(tileId, reactComp) {
    this.tileId = tileId;
    this.reactComp = reactComp;
    this.tileData = getTileData(tileId, reactComp);
    this.toggleLoading = this.toggleLoading.bind(this);
    this.handleOpts = this.handleOpts.bind(this);
    this.changeTileStatus = this.changeTileStatus.bind(this);
    this.addTileToCollection = this.addTileToCollection.bind(this);
    this.removeTileFromCollection = this.removeTileFromCollection.bind(this);
  }

  static fetchAllTiles(cb, activeStatus) {
    Fetcher.xmlHttpRequest({
      path: `/api/client_admin/tiles?status=${activeStatus}`,
      method: 'GET',
      success: resp => {
        cb(resp, constants.TILE_STATUS);
      },
    });
  }

  static fetchNewTile(tileId, reactComp) {
    reactComp.setState({ loading: true });
    fetchTileJson(tileId, resp => {
      const tiles = { ...reactComp.state.tiles };
      tiles.plan.tiles.unshift({ ...resp });
      tiles.plan.count += 1;
      reactComp.setState({ tiles, loading: false }, () => {
        reactComp.initializeDragAndDropContext();
      });
    });
  }

  static sortTiles(
    { tileId, leftTileId, rightTileId, planDate },
    showErrorNotification,
  ) {
    Fetcher.xmlHttpRequest({
      method: 'POST',
      path: `/api/client_admin/tiles/${tileId}/sorts`,
      params: {
        sort: {
          left_tile_id: leftTileId,
          right_tile_id: rightTileId,
          plan_date: planDate,
        },
      },
      err: err =>
        err.json().then(json => {
          const content = json.error || i18n.t('shared:errorText');
          showErrorNotification({ content });
        }),
    });
  }

  static batchPlanDateUpdate(ids, value, cb, insertionIndex) {
    Fetcher.xmlHttpRequest({
      method: 'POST',
      path: `/api/client_admin/tiles/update_plan_date`,
      params: { ids, value, insertion_index: insertionIndex },
      success: cb,
    });
  }

  static batchUpdateStatus(ids, value, cb) {
    Fetcher.xmlHttpRequest({
      method: 'POST',
      path: `/api/client_admin/tiles/update_status`,
      params: { ids, value },
      success: cb,
    });
  }

  static getTilesWhere(tiles, condition) {
    const key = Object.keys(condition)[0];
    const value = condition[key];
    const tileCollection = [];
    return tiles.reduce((result, tile) => {
      if (tile[key] === value) {
        result.push(tile);
      }
      return result;
    }, tileCollection);
  }

  static groupBy(collection, params) {
    const result = {};
    if (collection) {
      for (let i = 0; i < collection.length; i++) {
        result[collection[i][params]] = result[collection[i][params]] || [];
        result[collection[i][params]].push(collection[i]);
      }
    }
    return result;
  }

  handleOpts(opts) {
    Object.keys(opts).forEach(key => {
      if (key === 'setLoadingTo') {
        this.tileData.selectTile.loading = opts[key];
      }
    });
  }

  toggleLoading() {
    this.tileData.selectTile.loading = !this.tileData.selectTile.loading;
    this.reactComp.setState({ tiles: this.tileData.stateTiles }, () => {
      this.reactComp.initializeDragAndDropContext();
    });
  }

  removeSelectTileUsingIndex() {
    this.tileData.stateTiles[this.reactComp.state.activeStatus].tiles.splice(
      this.tileData.selectTileIndex,
      1,
    );
    this.tileData.stateTiles[this.reactComp.state.activeStatus].count -= 1;
  }

  addTileTo(status, tile, save) {
    this.toggleLoading();

    fetchTileJson(tile.id, response => {
      const updatedTile = { ...response };

      this.tileData.stateTiles[status].tiles.unshift(updatedTile);
      this.tileData.stateTiles[status].count += 1;

      if (save) {
        this.toggleLoading();
      }
    });
  }

  changeTileStatus(newStatus, activeStatus, count) {
    updateTileStatus(this.tileId, newStatus, () => {
      if (activeStatus === 'plan' || activeStatus === 'draft') {
        updateJquerySend(count, newStatus === 'draft');
      }
      if (newStatus === 'ignored' || newStatus === 'user_submitted') {
        this.toggleLoading();
        this.tileData.stateTiles[this.reactComp.state.activeStatus].tiles[
          this.tileData.selectTileIndex
        ].ignored = newStatus === 'ignored';
      } else {
        this.removeSelectTileUsingIndex();
        this.addTileTo(newStatus, this.tileData.selectTile, 'save');
      }
      this.reactComp.setState({ tiles: this.tileData.stateTiles }, () => {
        this.reactComp.initializeDragAndDropContext();
      });
    });
  }

  addTileToCollection(newTile, opts) {
    this.handleOpts(opts);
    this.addTileTo(this.reactComp.state.activeStatus, newTile);
    this.reactComp.setState({ tiles: this.tileData.stateTiles });
  }

  removeTileFromCollection() {
    this.removeSelectTileUsingIndex();
    this.reactComp.setState({ tiles: this.tileData.stateTiles }, () => {
      this.reactComp.initializeDragAndDropContext();
    });
  }

  refresh() {
    this.toggleLoading();
    fetchTileJson(this.tileId, resp => {
      const tiles = { ...this.tileData.stateTiles };
      tiles[this.reactComp.state.activeStatus].tiles[
        this.tileData.selectTileIndex
      ] = { ...resp };
      this.toggleLoading();
    });
  }

  performMenuAction(action) {
    const actions = {
      copy: resp => {
        this.addTileTo('plan', resp[0], 'save');
      },
      deleteConfirm: () => {
        this.removeTileFromCollection();
      },
    };
    const callback = resp => {
      actions[action](resp);
      this.reactComp.initializeDragAndDropContext();
    };
    const dbAction = action === 'deleteConfirm' ? deleteTile : performDbAction;

    this.toggleLoading();
    dbAction(this.tileId, action, callback);
  }
}

export default TileManager;
